import React, { useEffect, useState } from 'react';
import { Modal, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { formatDate } from '../../utils/dateUtils';
import { Add } from '@mui/icons-material';
import { Account, Segment, Transaction, TransactionCreate } from '../../interfaces/accounting.interface';
import { TransactionService } from '../../services/transaction.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px',
    maxHeight: '90vh',
    overflowY: 'auto' as const
};

const TransactionList: React.FC = () => {
    const toast = useToast();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filter state
    const [filterType, setFilterType] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');

    // Form state
    const [formData, setFormData] = useState<{
        transactionType: 'INCOME' | 'EXPENSE';
        amount: number;
        date: string;
        categoryAccountId: string;
        paymentAccountId: string;
        description: string;
        reference: string;
        segmentId: string;
    }>({
        transactionType: 'INCOME',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        categoryAccountId: '',
        paymentAccountId: '',
        description: '',
        reference: '',
        segmentId: ''
    });

    const fetchTransactions = async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await TransactionService.getTransactionsByInstitution(
                pageNum,
                10,
                filterType || undefined,
                filterStartDate || undefined,
                filterEndDate || undefined
            );
            setTransactions(response.content || []);
            setTotalPages(response.totalPages || 0);
            setPage(response.number || 0);
        } catch (err) {
            toast.showApiError(err, 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const data = await AccountService.getAllAccounts();
            setAccounts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    };

    const fetchSegments = async () => {
        try {
            const response = await SegmentService.getByInstitution();
            const active: Segment[] = (response.content || []).filter((s: Segment) => s.active !== false);
            setSegments(active);
        } catch (err) {
            console.error('Error fetching segments:', err);
        }
    };

    useEffect(() => {
        fetchTransactions(0);
        fetchAccounts();
        fetchSegments();
    }, []);

    const handleFilter = () => {
        fetchTransactions(0);
    };

    const revenueAccounts = accounts.filter(a => a.accountType === 'REVENUE');
    const expenseAccounts = accounts.filter(a => a.accountType === 'EXPENSE');
    const assetAccounts = accounts.filter(a => a.accountType === 'ASSET');

    const getCategoryAccounts = () => {
        return formData.transactionType === 'INCOME' ? revenueAccounts : expenseAccounts;
    };

    const handleOpenModal = () => {
        setFormData({
            transactionType: 'INCOME',
            amount: 0,
            date: new Date().toISOString().split('T')[0],
            categoryAccountId: '',
            paymentAccountId: '',
            description: '',
            reference: '',
            segmentId: ''
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const institutionId = AuthService.getSelectedInstitution();
            const transactionData: TransactionCreate = {
                transactionType: formData.transactionType,
                amount: Number(formData.amount),
                date: formData.date,
                categoryAccountId: formData.categoryAccountId,
                paymentAccountId: formData.paymentAccountId,
                description: formData.description || undefined,
                reference: formData.reference || undefined,
                institutionId,
                segmentId: formData.segmentId || undefined
            };
            await TransactionService.createTransaction(transactionData);
            await fetchTransactions(page);
            handleCloseModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to create transaction');
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Transactions</h2>
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenModal}>
                    Record Transaction
                </Button>
            </div>

            {/* Filter Bar */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" style={{ minWidth: '150px' }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={filterType}
                        label="Type"
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="INCOME">INCOME</MenuItem>
                        <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <Button variant="outlined" onClick={handleFilter}>
                    Filter
                </Button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Date</th>
                        <th style={tableHeaderStyle}>Type</th>
                        <th style={tableHeaderStyle}>Category</th>
                        <th style={tableHeaderStyle}>Amount</th>
                        <th style={tableHeaderStyle}>Description</th>
                        <th style={tableHeaderStyle}>Reference</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={6}>No transactions found</td>
                        </tr>
                    ) : (
                        transactions.map(txn => (
                            <tr key={txn.id}>
                                <td style={tableCellStyle}>{txn.date ? formatDate(txn.date) : '-'}</td>
                                <td style={tableCellStyle}>{txn.transactionType}</td>
                                <td style={tableCellStyle}>{txn.categoryAccountName || '-'}</td>
                                <td style={{
                                    ...tableCellStyle,
                                    color: txn.transactionType === 'INCOME' ? 'green' : 'red',
                                    fontWeight: 'bold'
                                }}>
                                    {txn.transactionType === 'INCOME' ? '+' : '-'}{txn.amount.toFixed(2)}
                                </td>
                                <td style={tableCellStyle}>{txn.description || '-'}</td>
                                <td style={tableCellStyle}>{txn.reference || '-'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
                <Button variant="outlined" disabled={page === 0} onClick={() => fetchTransactions(page - 1)}>
                    Previous
                </Button>
                <span>Page {page + 1} of {totalPages || 1}</span>
                <Button variant="outlined" disabled={page >= totalPages - 1} onClick={() => fetchTransactions(page + 1)}>
                    Next
                </Button>
            </div>

            {/* Record Transaction Modal */}
            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <div style={modalStyle}>
                    <h2>Record Transaction</h2>
                    <form onSubmit={handleSubmit}>
                        <FormControl fullWidth margin="normal" required>
                            <InputLabel>Transaction Type</InputLabel>
                            <Select
                                value={formData.transactionType}
                                label="Transaction Type"
                                onChange={(e) => setFormData({
                                    ...formData,
                                    transactionType: e.target.value as 'INCOME' | 'EXPENSE',
                                    categoryAccountId: ''
                                })}
                            >
                                <MenuItem value="INCOME">INCOME</MenuItem>
                                <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Amount"
                            type="number"
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                            required
                            margin="normal"
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                        <TextField
                            fullWidth
                            label="Date"
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            required
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                        <AccountAutocomplete
                            accounts={getCategoryAccounts()}
                            value={formData.categoryAccountId}
                            onChange={(id) => setFormData({ ...formData, categoryAccountId: id })}
                            label="Category Account"
                            required
                        />
                        <AccountAutocomplete
                            accounts={assetAccounts}
                            value={formData.paymentAccountId}
                            onChange={(id) => setFormData({ ...formData, paymentAccountId: id })}
                            label="Payment Account"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            label="Reference"
                            value={formData.reference}
                            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                            margin="normal"
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Segment</InputLabel>
                            <Select
                                value={formData.segmentId}
                                label="Segment"
                                onChange={(e) => setFormData({ ...formData, segmentId: e.target.value })}
                            >
                                <MenuItem value="">(default)</MenuItem>
                                {segments.map(s => (
                                    <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button onClick={handleCloseModal}>Cancel</Button>
                            <Button type="submit" variant="contained" color="primary">
                                Record
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default TransactionList;
